import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Homepage-only section theming for the single Header orb.
// Returns null on interior pages so the Header falls back to its default appearance.

export type HomepageOrbTheme = {
  id: string;
  // rgb triplet as string, e.g. "255 214 168"
  color: string;
  // overall brightness/opacity multiplier
  intensity: number;
  // halo radius multiplier applied via scale (1.0 = default)
  radius: number;
  // slow breathing (Labs)
  breathe: boolean;
  // subtle instability (Problem)
  flicker: boolean;
};

const THEMES: Record<string, HomepageOrbTheme> = {
  hero:            { id: "hero",           color: "255 200 150", intensity: 1.00, radius: 1.00, breathe: false, flicker: false },
  "what-we-do":    { id: "what-we-do",     color: "245 240 232", intensity: 0.85, radius: 0.95, breathe: false, flicker: false },
  problem:         { id: "problem",        color: "215 228 245", intensity: 0.60, radius: 0.78, breathe: false, flicker: true  },
  "system-teaser": { id: "system-teaser",  color: "240 244 250", intensity: 1.00, radius: 0.98, breathe: false, flicker: false },
  value:           { id: "value",          color: "255 214 168", intensity: 1.10, radius: 1.15, breathe: false, flicker: false },
  projects:        { id: "projects",       color: "250 232 208", intensity: 1.00, radius: 1.05, breathe: false, flicker: false },
  labs:            { id: "labs",           color: "255 240 214", intensity: 1.35, radius: 1.30, breathe: true,  flicker: false },
  vision:          { id: "vision",         color: "255 220 180", intensity: 1.00, radius: 1.05, breathe: false, flicker: false },
  cta:             { id: "cta",            color: "255 218 176", intensity: 1.05, radius: 1.08, breathe: false, flicker: false },
};

const SECTION_IDS = Object.keys(THEMES);

export const useHomepageOrbTheme = (): HomepageOrbTheme | null => {
  const { pathname } = useLocation();
  const enabled = pathname === "/";
  const [activeId, setActiveId] = useState<string>("hero");

  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let io: IntersectionObserver | null = null;

    // Sections mount lazily; retry until at least one is present.
    const attach = () => {
      const els = SECTION_IDS
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => !!el);
      if (!els.length) {
        raf = window.requestAnimationFrame(attach);
        return;
      }
      const root = document.querySelector("[data-scroll-container]") as HTMLElement | null;
      io = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (visible[0]) setActiveId(visible[0].target.id);
        },
        {
          root,
          rootMargin: "-35% 0px -35% 0px",
          threshold: [0, 0.25, 0.5, 0.75, 1],
        },
      );
      els.forEach((el) => io!.observe(el));
    };
    attach();
    return () => {
      window.cancelAnimationFrame(raf);
      io?.disconnect();
    };
  }, [enabled]);

  if (!enabled) return null;
  return THEMES[activeId] ?? THEMES.hero;
};
